import type { TemplateDefinition } from "@/components/AIComponents/ExportablesModal/templates/registry";
import type { ExportablesDataTypes } from "@/types/models/Exportables.interface";

/**
 * Builds the payload for assigning graphic organizers to an assignment.
 *
 * This function takes selected graphic organizer templates and their associated
 * form data, then constructs a payload suitable for sending to the API to save
 * the graphic organizers for a specific assignment.
 *
 * @param {Object} params
 * @param {Array} params.templates - Array of graphic organizer templates to be assigned
 * @param {Object} params.formData - Object containing form data for each template, keyed by seti_identifier
 * @param {string} [params.updatedAssignmentId] - The assignment content_id (optional, only included if present)
 * @param {string} params.userId - The unique_id of the user creating the assignment
 * @param {string} params.contentId - The id of the content being assigned
 *
 * @returns {Object} Payload object containing:
 *   - assignment_id (optional): The assignment's content_id
 *   - created_by: User's unique_id
 *   - content_id: Content id
 *   - graphic_organizers: Array of graphic organizer objects, each containing:
 *     - graphic_organizer_type: Type identifier (e.g., "graphic_organizer_anticipation_guide")
 *     - data: User-submitted form data for that specific organizer
 */

const buildAssignedGraphicOrganizerPayload = ({
  templates,
  formData,
  updatedAssignmentId,
  userId,
  contentId,
}: {
  templates: TemplateDefinition<ExportablesDataTypes>[];
  formData: any;
  updatedAssignmentId?: string | undefined;
  userId: string | undefined;
  contentId: string;
}) => {
  const templateData = templates.map(
    (assignedTemplate: TemplateDefinition<ExportablesDataTypes>) => {
      const { seti_identifier: setiIdentifier } = assignedTemplate;

      return {
        graphic_organizer_type: `graphic_organizer_${setiIdentifier}`,
        data: formData?.[setiIdentifier] ?? {},
      };
    },
  );

  const payload = {
    ...(updatedAssignmentId && {
      assignment_id: updatedAssignmentId,
    }),
    created_by: userId,
    content_id: contentId,
    graphic_organizers: templateData,
  };

  return payload;
};

export default buildAssignedGraphicOrganizerPayload;
